import { useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const readSize = (file) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => { res({ width: img.naturalWidth, height: img.naturalHeight }); URL.revokeObjectURL(img.src); };
  img.onerror = rej;
  img.src = URL.createObjectURL(file);
});

export const MapUploadDialog = ({ open, onOpenChange, uid, onSaved }) => {
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) return toast.error("Image exceeds 15 MB");
    setBusy(true);
    try {
      const { width, height } = await readSize(file);
      const fd = new FormData();
      fd.append("name", name || file.name.replace(/\.[^.]+$/, ""));
      fd.append("width", width);
      fd.append("height", height);
      fd.append("file", file);
      const { data } = await api.post(`/universes/${uid}/maps`, fd);
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="map-upload-dialog">
        <form onSubmit={save}>
          <DialogHeader><DialogTitle className="font-display text-3xl">Add a map</DialogTitle></DialogHeader>
          <div className="space-y-4 py-6">
            <div className="space-y-2"><Label>Map name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="The Known World" data-testid="map-name-input" /></div>
            <label className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-[#2D3442] p-8 cursor-pointer hover:border-[#D97706]/60 transition-colors">
              <Upload className="h-6 w-6 text-[#D97706]" />
              <span className="text-sm text-[#E8DFD1]">{file ? file.name : "Choose PNG, JPG or WebP (max 15 MB)"}</span>
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="map-file-input" />
            </label>
          </div>
          <DialogFooter><Button type="submit" disabled={busy || !file} data-testid="map-upload-button">{busy ? "Uploading…" : "Upload map"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
